/* eslint-disable no-console */
/* eslint-disable no-unreachable */
import { useState } from 'react';
import api from 'service/api';
import { useAuth } from '../auth';
import moment from 'moment';

export const useAgenda = () => {
  const { requestIntercept } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(0);
  const [status, setStatus] = useState('');

  const getJoinedStringScales = (scales) => {
    let joinedStringScales = scales
      .map((item) => item.value)
      .join('&scale_id=');
    return (joinedStringScales =
      scales.length > 0 ? `scale_id=${joinedStringScales}` : '');
  };

  const processResponseData = (data, period) =>
    new Promise((resolve, reject) => {
      try {
        if (data && data.length > 0) {
          const processedData = data.map((d) => [
            ...d.map((item) => ({
              ...item,
              ...period,
              schedules: [
                ...item?.schedules.map((sched) => [
                  ...sched.map((s) => ({
                    ...s,
                    agenda: item?.agendas.filter(
                      (ag) => ag.schedule_id === s.id
                    ),
                  })),
                ]),
              ],
            })),
          ]);
          resolve(processedData);
        } else {
          resolve([]);
        }
      } catch (err) {
        reject([]);
      }
    });

  const getAgenda = async (params, showLoading = true) => {
    try {
      setLoading(showLoading);
      const response = await requestIntercept(
        api.get,
        `/v2/admin/agendas`,
        params
      );
      if (response.status !== 200) {
        throw response;
      }
      const processedData = await processResponseData(response?.data?.data, {
        date_start: params.date_start,
        date_end: params.date_end,
      });
      setData(processedData);
      setTotal(response?.data?.total);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error);
    }
  };

  const getAgendaMultiScale = async (params, showLoading = true) => {
    try {
      setLoading(showLoading);
      const { scales, ...restParams } = params; // Destructure to remove scales from params
      const response = await requestIntercept(
        api.get,
        `/v2/admin/agendas?${getJoinedStringScales(scales)}`,
        restParams // Pass params without scales
      );
      if (response.status !== 200) {
        throw response;
      }
      const processedData = await processResponseData(response?.data?.data, {
        date_start: params.date_start,
        date_end: params.date_end,
      });
      setData(processedData[0]);
      setTotal(response?.data?.total);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error);
    }
  };

  const createAgenda = async (formData) =>
    new Promise((resolve, reject) => {
      (async function () {
        try {
          setLoading(true);

          const yearMonth = formData.yearMonth;

          const response = await requestIntercept(api.post, `/v1/agendas`, {
            ...formData,
            subsequent: null,
            yearMonth: null,
            month_day_start: moment(
              `${yearMonth?.year}-${yearMonth?.month}`,
              'yyyy-MM'
            )
              .startOf('month')
              .format('yyyy-MM-DD'),
            month_day_end: moment(
              `${yearMonth?.year}-${yearMonth?.month}`,
              'yyyy-MM'
            )
              .endOf('month')
              .format('yyyy-MM-DD'),
          });

          if (response.status !== 200 && response.status !== 201) {
            throw response;
          }

          setTimeout(() => {
            setLoading(false);
            resolve(response);
          }, 500);
        } catch (error) {
          setLoading(false);
          setError(error);
          reject('error');
        }
      })();
    });

  const deleteAgenda = async ({ id, comment }) =>
    new Promise((resolve, reject) => {
      (async function () {
        try {
          setLoading(true);
          const response = await requestIntercept(
            api.delete,
            `/v1/agendas/${id}`,
            {},
            {
              data: {
                comment,
              },
            },
            true
          );

          if (response?.status !== 200 && response?.status !== 201) {
            throw response;
          }
          setTimeout(() => {
            setLoading(false);
            resolve(response);
          }, 500);
        } catch (error) {
          setLoading(false);
          setError(error);
          reject(error);
        }
      })();
    });

  const deleteManyAgendas = async ({ agendas, comment }) =>
    new Promise((resolve, reject) => {
      (async function () {
        try {
          setLoading(true);
          const response = await requestIntercept(
            api.delete,
            `/v2/admin/agendas`,
            {},
            {
              data: {
                comment,
                agendas,
              },
            }
          );

          if (response?.status !== 200 && response?.status !== 201) {
            throw response;
          }
          setTimeout(() => {
            setLoading(false);
            resolve(response);
          }, 500);
        } catch (error) {
          setLoading(false);
          setError(error);
          reject(error);
        }
      })();
    });

  const validateManyAgendas = async ({
    validated_description,
    validated_status,
    agendas,
  }) =>
    new Promise((resolve, reject) => {
      (async function () {
        try {
          setLoading(true);
          const response = await requestIntercept(
            api.put,
            '/v2/admin/agendas/validate',
            {
              validated_description,
              validated_status,
              agendas,
            }
          );

          if (response?.status !== 200 && response?.status !== 201) {
            throw response;
          }

          setTimeout(() => {
            setLoading(false);
            resolve(response);
          }, 500);
        } catch (error) {
          setLoading(false);
          setError(error);
          reject(error);
        }
      })();
    });

  const [hoursData, setHoursData] = useState([]);
  const [totalHours, setTotalHours] = useState(0); // total de itens no backend
  const [loadingHours, setLoadingHours] = useState(false);
  const getHoursByScale = async ({
    user_id = null,
    scale_id = null,
    date_start = null,
    date_end = null,
    page = 1,
    per_page = 12,
  }) => {
    if (!user_id && !scale_id) return;

    try {
      setLoadingHours(true);

      const params = {
        ...(user_id && { user_id }),
        ...(scale_id && { scale_id }),
        ...(date_start && { date_start }),
        ...(date_end && { date_end }),
        page,
        per_page,
      };

      const response = await requestIntercept(
        api.get,
        '/v2/admin/agendas/hours',
        params
      );

      if (response.status !== 200) throw response;

      setHoursData(response.data?.data || []);
      setTotalHours(response.data?.pagination?.total_items || 0);
    } catch (err) {
      console.error('Erro ao buscar horas por escala:', err);
      setHoursData([]);
      setTotalHours(0);
    } finally {
      setLoadingHours(false);
    }
  };

  return {
    loading,
    data,
    hoursData,
    loadingHours,
    totalHours,
    total,
    error,
    status,
    setData,
    setStatus,
    getAgenda,
    createAgenda,
    deleteAgenda,
    deleteManyAgendas,
    validateManyAgendas,
    getAgendaMultiScale,
    getHoursByScale,
  };
};
